import type { ImageObject, TextObject, ContainerObject, Size } from './project/posterSchema'

// ── Field data record ──────────────────────────────────────────────────────────
//
// The data an object's field bindings resolve against — e.g. an event or special.
// (Resolved theme variables travel separately as a ThemeRecord, not merged in here;
// see themeVariables.) It is an open dictionary because its keys are the caller's field
// names, walked by dotted path (resolveField) and interpolated into templates; values
// stay `unknown` since a field may hold a string, number, image value, etc.
export type DataRecord = Record<string, unknown>

// ── Runtime object types ───────────────────────────────────────────────────────
//
// These extend the Zod-derived schema types with runtime-only fields (id,
// isField). ContainerObject's objects[] is overridden to use RuntimeObject[] so
// the tree is uniformly typed at runtime. RuntimeText adds size? for the
// wrap-width property that is set by the UI but not in the schema.

// Stored on a plain image when it was uploaded via the poster builder.
// The URL is derived at render time from this data using Urls.imageUrl() so it
// remains correct across environments (dev / production / proxy-assets).
export interface UploadedImageData {
	hash:    string
	formats: Record<string, { format: string } | undefined>
	dev:     boolean
	folder:  string   // e.g. 'Event-image' | 'Special-image'
	shared:  boolean  // stored under __SHARED rather than the site key — must match the read URL
}

export interface RuntimeImage extends ImageObject {
	id: string
	isField?: boolean
	image?: UploadedImageData
}

export interface RuntimeText extends TextObject {
	id: string
	isField?: boolean
	size?: Size          // Field reserved-box width/height — runtime only, not in TextObjectSchema.
	                     // Plain text stores no size; it wraps via softWrapPositions (see schema).
}

export interface RuntimeContainer extends Omit<ContainerObject, 'objects'> {
	id: string
	isField?: boolean
	objects?: RuntimeObject[]
}

export type RuntimeObject = RuntimeImage | RuntimeText | RuntimeContainer

// ── Command pattern ───────────────────────────────────────────────────────────

export interface Command {
	label?: string
	mergeKey?: string
	execute(): void
	undo(): void
}

export type Position = {
	x: number,
	y: number
};

export type Dimensions = {
	width: number,
	height: number
};

export type Box = {
	pos: Position,
	dims: Dimensions
};


// ── Geometry ──────────────────────────────────────────────────────────────────

// The cover-fit image rect (box) plus the max pan offsets that keep it covering the viewport.
export type LayoutCalc = {
	box: Box
	maxPanX: number
	maxPanY: number
}

// ── Drag / resize interaction state ───────────────────────────────────────────

export interface DragState {
	start: Position,
	onUpdate: (dx: number, dy: number) => void,
	onEnd: () => void
}

export type ResizeState = DragState

// ── Field links ───────────────────────────────────────────────────────────────

export interface FieldLink {
	userId:   string
	isCut:    boolean
	type?:    'text' | 'image'
	template?: string    // Text template with {field.path} placeholders
	field?:   string     // Image field path for dynamic resolution
}

// ── Box helpers ───────────────────────────────────────────────────────────────

// A detached deep copy of a box. Needed wherever a box is read back later than it is captured —
// the live box is a Solid store, so a shallow copy would still resolve pos/dims through the store
// and see any write made in between.
export function cloneBox(box: Box): Box
{
	return { pos: { ...box.pos }, dims: { ...box.dims } }
}

// Component-wise equality — "this gesture changed nothing", as against object identity, which two
// reads of the same live box never share.
export function sameBox(a: Box, b: Box): boolean
{
	return a.pos.x === b.pos.x && a.pos.y === b.pos.y
		&& a.dims.width === b.dims.width && a.dims.height === b.dims.height
}

